import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/services/firebase/config';
import { useAuthStore } from '@/store/authStore';
import type { BaseUser } from '@/types/user.types';

/**
 * Hook principal d'authentification.
 * S'abonne aux changements d'état Firebase Auth et charge
 * les données Firestore de l'utilisateur connecté.
 * À monter une seule fois dans App.tsx.
 */
export function useAuthListener(): void {
  const { setUser, setLoading, clearUser } = useAuthStore();

  useEffect(() => {
    setLoading(true);

    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (!firebaseUser) {
        clearUser();
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as BaseUser;
          setUser(firebaseUser, userData);
        } else {
          // Utilisateur Firebase Auth sans document Firestore
          // (peut arriver si l'inscription a échoué à mi-chemin)
          setUser(firebaseUser, null);
        }
      } catch {
        // Erreur Firestore — on garde l'auth mais sans userData
        setUser(firebaseUser, null);
      }
    });

    return () => unsubscribe();
  }, [setUser, setLoading, clearUser]);
}

/**
 * Hook de consommation pour les composants.
 * Retourne l'état auth depuis le store Zustand.
 */
export function useAuth() {
  return useAuthStore();
}
